import React, { lazy, useEffect, Suspense, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { observer } from "mobx-react";

import { ThemeProvider } from "react-jss";
import {
  Themes,
  Loader,
  NotificationProvider,
  ThemeProviderMUI,
} from "shipnow-mercurio";

import AppStore from "./stores/AppStore";
import AppContext from "./AppContext";

import {
  ToastProvider,
  DefaultToastContainer,
} from "react-toast-notifications";

import MediaQuery from "react-responsive";
import { retry } from "./lib/helpers";
import { CacheBuster } from "./components/Common";
import "./styles/styles.css";

import moment from "moment-timezone";

import { useNotifications } from "./assets/constants/notifications";
import { useSentry } from "./hooks/useSentry";
import { useStore } from "./_v2/stores/AppStore";
import { useSession } from "./_v2/modules/session/hooks/useSession";

const TrackerRouter = lazy(() =>
  retry(() => import("./views/Tracker/TrackerRouter"))
);

const AppRouter = lazy(() =>
  retry(() => import("./views/AppRouter/AppRouter"))
);
const AppRouterMobile = lazy(() =>
  retry(() => import("./views/AppRouter/AppRouterMobile"))
);
const SessionRouter = lazy(() =>
  retry(() => import("./_v2/modules/router/SessionRouter/SessionRouter"))
);

const customToastContainer = ({ children, ...props }) => (
  <div className="custom-toast-container">
    <DefaultToastContainer {...props} children={children} />
  </div>
);
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.children.props?.children[0]?.key ===
    nextProps.children.props?.children[0]?.key
  );
};
const MemoizedToast = React.memo(customToastContainer, areEqual);

moment.tz.setDefault("America/Argentina/Buenos_Aires");

const App = observer(() => {
  const [appStore] = useState(() => new AppStore());
  const { initSentry } = useSentry();
  const { handleLoginWithCookie } = useSession(appStore);

  const store = useStore();

  const arrayNotifications = useNotifications();

  const TOAST_CONTAINER = { ToastContainer: MemoizedToast };

  const loader = (
    <Loader animation="spin" className="fullscreen" label="Cargando..." />
  );

  useEffect(() => {
    initSentry();
  }, []); // eslint-disable-line

  useEffect(() => {
    handleLoginWithCookie();
  }, []); // eslint-disable-line

  return (
    <AppContext.Provider value={appStore}>
      <ThemeProvider theme={Themes[appStore.getCurrentTheme()]}>
        <ThemeProviderMUI>
          <CacheBuster fallback={loader}>
            <ToastProvider
              components={TOAST_CONTAINER}
              placement="bottom-right"
              autoDismissTimeout={6000}
            >
              <NotificationProvider
                list={arrayNotifications}
                onChangeNewsCounter={(counter) => {
                  store.ui.setNewsCounter(counter);
                }}
              >
                {store.isLoggingIn() ? (
                  loader
                ) : (
                  <MediaQuery minDeviceWidth={769}>
                    {(desk) => (
                      <Suspense fallback={loader}>
                        <Switch>
                          <Route
                            path="/track"
                            component={(props) => (
                              <TrackerRouter isMobile={!desk} />
                            )}
                          />
                          <Route path="/session" component={SessionRouter} />

                          <Route
                            path="/app"
                            component={desk ? AppRouter : AppRouterMobile}
                          />

                          <Redirect from="/tracker/:id" to="/track/:id" />
                          <Redirect from="/tracker" to="/track" />
                          <Redirect from="/orders/:id" to="/app/orders/:id" />
                          <Redirect from="/orders" to="/app/orders" />

                          {/* Replace this by a 404... maybe.... */}
                          <Redirect
                            to="/app"
                            component={desk ? AppRouter : AppRouterMobile}
                          />
                        </Switch>
                      </Suspense>
                    )}
                  </MediaQuery>
                )}
              </NotificationProvider>
            </ToastProvider>
          </CacheBuster>
        </ThemeProviderMUI>
      </ThemeProvider>
    </AppContext.Provider>
  );
});

export default App;
